import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FilterSelect } from '../components/charts/FilterControls';
import { CHART_COLORS, formatUsd } from '../components/charts/chartUtils';
import DashboardLayout from '../components/DashboardLayout';
import { useProject } from '../contexts/ProjectContext';
import { dashboardService } from '../services/dashboard';

const DAY_OPTIONS = [
  { id: 1, label: '1D' },
  { id: 7, label: '7d' },
  { id: 30, label: '30d' },
  { id: 90, label: '90d' },
];

const ROLE_LINE_COLORS = {
  routed: CHART_COLORS.eval_routed,
  original: CHART_COLORS.original,
  higher: CHART_COLORS.savings,
  lower: CHART_COLORS.eval,
};

const MODEL_LINE_COLORS = [
  CHART_COLORS.eval_routed,
  CHART_COLORS.original,
  CHART_COLORS.savings,
  CHART_COLORS.eval,
  CHART_COLORS.accent,
  CHART_COLORS.cost,
];

const ROLE_LABELS = {
  routed: 'Routed',
  original: 'Original',
  higher: 'Higher',
  lower: 'Lower',
};

const ROLE_LINE_ORDER = ['original', 'routed', 'higher', 'lower'];

const LEGEND_COLLAPSED_COUNT = 8;

function modelLineColor(model, lineModels) {
  const index = lineModels.indexOf(model);
  return MODEL_LINE_COLORS[index >= 0 ? index % MODEL_LINE_COLORS.length : 0];
}

function scoreColor(score) {
  if (score == null) return 'opacity-50';
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  return 'text-red-400';
}

export default function EvalsPage() {
  const { selectedHash, selectedProject } = useProject();
  const navigate = useNavigate();
  const { runId } = useParams();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [model, setModel] = useState('');
  const [detailRun, setDetailRun] = useState(null);
  const [chartByModel, setChartByModel] = useState(false);
  const [chartModelSelection, setChartModelSelection] = useState([]);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [allRunsOpen, setAllRunsOpen] = useState(false);
  const [allRunsPage, setAllRunsPage] = useState(1);
  const [allRuns, setAllRuns] = useState(null);

  const load = () => {
    if (!selectedHash) return;
    dashboardService.evalsDashboard(selectedHash, { days, model }, (res) => setData(res.data), console.error);
  };

  useEffect(() => { load(); }, [selectedHash, days, model]);

  useEffect(() => {
    setModel('');
  }, [selectedHash]);

  useEffect(() => {
    setAllRunsPage(1);
    setAllRuns(null);
    setAllRunsOpen(false);
  }, [days, model]);

  useEffect(() => {
    if (!runId) {
      setDetailRun(null);
      return;
    }
    dashboardService.evalRunDetail(
      runId,
      (res) => setDetailRun(res.data),
      () => navigate('/evals/', { replace: true }),
    );
  }, [runId, navigate]);

  const loadAllRuns = useCallback(() => {
    if (!selectedHash) return;
    dashboardService.evalsRuns(
      selectedHash,
      { days, model, page: allRunsPage, page_size: 20 },
      (res) => setAllRuns(res.data),
      console.error,
    );
  }, [selectedHash, days, model, allRunsPage]);

  useEffect(() => {
    if (allRunsOpen) loadAllRuns();
  }, [allRunsOpen, loadAllRuns]);

  const roleLineData = useMemo(() => {
    const byRole = data?.quality_over_time_by_role;
    if (!byRole) return [];
    const dates = new Set();
    Object.values(byRole).forEach((rows) => rows.forEach((r) => dates.add(r.date)));
    return [...dates].sort().map((date) => {
      const row = { date };
      Object.entries(byRole).forEach(([role, rows]) => {
        const match = rows.find((r) => r.date === date);
        if (match) row[role] = match.score;
      });
      return row;
    });
  }, [data]);

  const modelLineData = useMemo(() => {
    if (!data?.quality_over_time) return [];
    const dates = new Set();
    Object.values(data.quality_over_time).forEach((rows) => rows.forEach((r) => dates.add(r.date)));
    return [...dates].sort().map((date) => {
      const row = { date };
      Object.entries(data.quality_over_time).forEach(([model, rows]) => {
        const match = rows.find((r) => r.date === date);
        if (match) row[model] = match.score;
      });
      return row;
    });
  }, [data]);

  const lineModels = useMemo(
    () => (data?.quality_over_time ? Object.keys(data.quality_over_time) : []),
    [data],
  );

  const roleLineRoles = useMemo(() => {
    const byRole = data?.quality_over_time_by_role;
    if (!byRole) return ['original', 'routed'];
    return ROLE_LINE_ORDER.filter((role) => byRole[role]?.length);
  }, [data]);

  const visibleLineModels = useMemo(() => {
    if (!chartModelSelection.length) return lineModels;
    return lineModels.filter((m) => chartModelSelection.includes(m));
  }, [lineModels, chartModelSelection]);

  useEffect(() => {
    setChartModelSelection([]);
    setLegendExpanded(false);
  }, [chartByModel, lineModels.join('|')]);

  const toggleChartModel = useCallback((name) => {
    setChartModelSelection((prev) => {
      if (!prev.length) return [name];
      if (prev.includes(name)) return prev.filter((m) => m !== name);
      return [...prev, name];
    });
  }, []);

  const recentRuns = data?.recent_runs || [];
  const allRunsCount = allRuns?.count || 0;
  const allRunsPageSize = allRuns?.page_size || 20;
  const totalAllRunsPages = Math.max(1, Math.ceil(allRunsCount / allRunsPageSize));

  const barChartHeight = Math.max(220, (data?.by_model?.length || 0) * 28);

  const openRun = (id) => {
    setAllRunsOpen(false);
    navigate(`/evals/runs/${id}/`);
  };

  if (selectedProject && !selectedProject.eval_enabled) {
    return (
      <DashboardLayout title="Evals">
        <p className="text-sm opacity-70 mb-4">Shadow evals are off for this project.</p>
        <Link to="/settings/projects/" className="text-theme-primary text-sm">Enable in Settings →</Link>
      </DashboardLayout>
    );
  }

  const wlt = data?.win_loss_tie;
  const savings = data?.savings_recommendation;

  return (
    <DashboardLayout title="Evals">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex flex-wrap gap-3">
          {DAY_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setDays(opt.id)}
              className={`px-3 py-1 rounded-full text-sm ${days === opt.id ? 'bg-theme-primary text-theme-button' : 'bg-theme-secondary'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <FilterSelect
          label="Model"
          value={model}
          onChange={setModel}
          options={(data?.filter_options?.models || []).map((m) => ({ value: m, label: m }))}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <div className="p-5 rounded-2xl border border-theme-secondary bg-theme-secondary/20">
          <div className="text-xs opacity-60 mb-1">Routing quality</div>
          {wlt ? (
            <p className="text-sm leading-relaxed">
              Routed wins <span className="font-medium">{wlt.win_pct}%</span>
              {' · '}ties <span className="font-medium">{wlt.tie_pct}%</span>
              {' · '}loses <span className="font-medium">{wlt.loss_pct}%</span>
            </p>
          ) : (
            <p className="text-sm opacity-50">No original vs routed pairs yet</p>
          )}
        </div>

        <div className="p-5 rounded-2xl border border-theme-secondary bg-theme-secondary/20">
          <div className="text-xs opacity-60 mb-1">Overall quality</div>
          <div className={`text-3xl font-semibold ${scoreColor(data?.overall_score)}`}>
            {data?.overall_score != null ? `${data.overall_score}%` : '—'}
          </div>
          {data?.overall_trend != null && (
            <div className="text-xs mt-1 opacity-70">
              {data.overall_trend >= 0 ? '+' : ''}{data.overall_trend} vs prior period
            </div>
          )}
        </div>

        <div className="p-5 rounded-2xl border border-theme-secondary bg-theme-secondary/20">
          <div className="text-xs opacity-60 mb-1">Eval cost</div>
          <div className="text-3xl font-semibold tabular-nums">
            {formatUsd(data?.eval_cost_usd ?? 0)}
          </div>
          <div className="text-xs mt-1 opacity-70">
            {data?.eval_cost_usd ? `Last ${days} days` : 'No eval spend in this period'}
          </div>
        </div>

        {savings && (
          <div className="p-5 rounded-2xl border border-theme-secondary bg-theme-secondary/20">
            <div className="text-xs opacity-60 mb-1">Savings opportunity</div>
            <p className="text-sm mb-3">
              Switch <span className="font-mono">{savings.input_model}</span> to cost effective
              ({savings.suggested_model}): save ~${savings.monthly_savings_usd}/mo
              with {savings.quality_impact_pct}% quality impact.
            </p>
            <button
              type="button"
              onClick={() => navigate(
                `/routing/${encodeURIComponent(savings.input_model)}/?suggested_tier=${encodeURIComponent(savings.suggested_tier)}`,
              )}
              className="text-xs px-3 py-1.5 rounded-lg bg-theme-primary text-theme-button"
            >
              Review in Routing
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="p-4 rounded-2xl border border-theme-secondary">
          <h3 className="text-sm font-medium mb-4">Quality by model</h3>
          <ResponsiveContainer width="100%" height={barChartHeight}>
            <BarChart layout="vertical" data={data?.by_model || []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="model" width={140} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="avg_score" fill="var(--theme-primary)" name="Avg score" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="p-4 rounded-2xl border border-theme-secondary">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium">Quality over time</h3>
            <button
              type="button"
              onClick={() => setChartByModel((v) => !v)}
              className="text-xs text-theme-primary"
            >
              {chartByModel ? 'By role' : 'By model'}
            </button>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartByModel ? modelLineData : roleLineData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => formatPeriodLabel(value, data?.segment)}
                />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={32} />
                <Tooltip labelFormatter={(value) => formatPeriodLabel(value, data?.segment)} />
                {!chartByModel && <Legend wrapperStyle={{ fontSize: 11 }} />}
                {chartByModel ? (
                  visibleLineModels.map((modelName) => (
                    <Line
                      key={modelName}
                      type="monotone"
                      dataKey={modelName}
                      dot={false}
                      strokeWidth={2}
                      stroke={modelLineColor(modelName, lineModels)}
                    />
                  ))
                ) : (
                  roleLineRoles.map((role) => (
                    <Line
                      key={role}
                      type="monotone"
                      dataKey={role}
                      name={ROLE_LABELS[role]}
                      dot={false}
                      strokeWidth={2}
                      stroke={ROLE_LINE_COLORS[role]}
                    />
                  ))
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          {chartByModel && lineModels.length > 0 && (
            <ChartModelLegend
              models={lineModels}
              selection={chartModelSelection}
              onToggle={toggleChartModel}
              onClear={() => setChartModelSelection([])}
              expanded={legendExpanded}
              onExpandedChange={setLegendExpanded}
            />
          )}
        </div>
      </div>

      <div className="p-4 rounded-2xl border border-theme-secondary">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <h3 className="text-sm font-medium">Recent Evals</h3>
          <button
            type="button"
            onClick={() => {
              setAllRunsPage(1);
              setAllRunsOpen(true);
            }}
            className="text-xs text-theme-primary"
          >
            View all
          </button>
        </div>
        <EvalRunsTable runs={recentRuns} onSelect={openRun} emptyMessage="No eval runs in this period." />
      </div>

      {allRunsOpen && (
        <AllRunsModal
          runs={allRuns?.results || []}
          page={allRunsPage}
          totalPages={totalAllRunsPages}
          loading={!allRuns}
          onClose={() => setAllRunsOpen(false)}
          onSelect={openRun}
          onPageChange={setAllRunsPage}
        />
      )}

      {detailRun && runId && (
        <EvalDetailModal
          run={detailRun}
          onClose={() => navigate('/evals/')}
          onSwitchRun={(id) => navigate(`/evals/runs/${id}/`)}
        />
      )}
    </DashboardLayout>
  );
}

function EvalRunsTable({ runs, onSelect, emptyMessage }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left opacity-60 border-b border-theme-secondary">
          <th className="py-2">Request</th>
          <th className="py-2">Model</th>
          <th className="py-2">Role</th>
          <th className="py-2">Score</th>
          <th className="py-2">When</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((row) => (
          <tr
            key={row.id}
            className="border-b border-theme-secondary/50 cursor-pointer hover:bg-theme-secondary/20"
            onClick={() => onSelect(row.id)}
          >
            <td className="py-2 max-w-xs truncate opacity-80" title={row.prompt_preview}>
              {row.prompt_preview || row.input_model}
            </td>
            <td className="py-2 font-mono text-xs">{row.model_tested}</td>
            <td className="py-2">{ROLE_LABELS[row.model_role] || row.model_role}</td>
            <td className={`py-2 ${scoreColor(row.score)}`}>
              {row.score != null ? `${row.score}%` : '—'}
            </td>
            <td className="py-2 opacity-60 text-xs">{new Date(row.created_at).toLocaleString()}</td>
          </tr>
        ))}
        {runs.length === 0 && (
          <tr>
            <td colSpan={5} className="py-8 text-center opacity-50">{emptyMessage}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function AllRunsModal({ runs, page, totalPages, loading, onClose, onSelect, onPageChange }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl max-h-[85vh] overflow-auto rounded-2xl bg-theme-main border border-theme-secondary p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">All eval runs</h2>
          <button type="button" onClick={onClose} className="opacity-60">✕</button>
        </div>
        {loading ? (
          <p className="py-8 text-center opacity-50">Loading…</p>
        ) : (
          <>
            <EvalRunsTable runs={runs} onSelect={onSelect} emptyMessage="No eval runs in this period." />
            {totalPages > 1 && (
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => onPageChange(page - 1)}
                  className="text-sm px-3 py-1 border rounded-lg border-theme-secondary disabled:opacity-40"
                >
                  Prev
                </button>
                <span className="text-sm opacity-60 self-center">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => onPageChange(page + 1)}
                  className="text-sm px-3 py-1 border rounded-lg border-theme-secondary disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatPeriodLabel(period, segment) {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  if (segment === 'hour') {
    return date.toLocaleString(undefined, { month: 'numeric', day: 'numeric', hour: 'numeric' });
  }
  return date.toLocaleString(undefined, { month: 'numeric', day: 'numeric' });
}

function ChartModelLegend({ models, selection, onToggle, onClear, expanded, onExpandedChange }) {
  const filtering = selection.length > 0;
  const collapsed = !expanded && models.length > LEGEND_COLLAPSED_COUNT;
  const visibleModels = collapsed ? models.slice(0, LEGEND_COLLAPSED_COUNT) : models;
  const hiddenCount = models.length - LEGEND_COLLAPSED_COUNT;

  return (
    <div className="mt-3 pt-3 border-t border-theme-secondary/50">
      <div className={`flex flex-wrap items-center gap-1.5 ${expanded ? 'max-h-40 overflow-y-auto' : ''}`}>
        {visibleModels.map((name) => {
          const color = modelLineColor(name, models);
          const selected = filtering && selection.includes(name);
          const dimmed = filtering && !selection.includes(name);
          return (
            <button
              key={name}
              type="button"
              title={name}
              onClick={() => onToggle(name)}
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border transition-opacity ${
                selected
                  ? 'border-theme-primary bg-theme-primary/10 opacity-100'
                  : dimmed
                    ? 'border-theme-secondary opacity-35 hover:opacity-60'
                    : 'border-theme-secondary opacity-80 hover:opacity-100'
              }`}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
              <span className="font-mono truncate max-w-[140px]">{name}</span>
            </button>
          );
        })}
        {collapsed && hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => onExpandedChange(true)}
            className="text-xs text-theme-primary px-2 py-0.5"
          >
            +{hiddenCount} more
          </button>
        )}
        {expanded && models.length > LEGEND_COLLAPSED_COUNT && (
          <button
            type="button"
            onClick={() => onExpandedChange(false)}
            className="text-xs text-theme-primary px-2 py-0.5"
          >
            Show less
          </button>
        )}
        {filtering && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-theme-primary px-2 py-0.5"
          >
            Show all
          </button>
        )}
      </div>
    </div>
  );
}

function EvalDetailModal({ run, onClose, onSwitchRun }) {
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [schemaExpanded, setSchemaExpanded] = useState(false);
  const [responseExpanded, setResponseExpanded] = useState(false);
  const event = run.usage_event || {};
  const promptText = event.messages?.length
    ? event.messages.map((m) => `${m.role}: ${m.content}`).join('\n')
    : event.prompt;
  const schemaText = event.generation_params?.response_format
    ? JSON.stringify(event.generation_params.response_format, null, 2)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] overflow-auto rounded-2xl bg-theme-main border border-theme-secondary p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-medium">Eval detail</h2>
          <button type="button" onClick={onClose} className="opacity-60">✕</button>
        </div>
        {run.comparison_runs?.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-theme-secondary">
            {run.comparison_runs.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onSwitchRun(r.id)}
                className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                  r.id === run.id
                    ? 'border-theme-primary bg-theme-secondary/50'
                    : 'border-theme-secondary opacity-80 hover:opacity-100'
                } ${scoreColor(r.score)}`}
              >
                {ROLE_LABELS[r.model_role] || r.model_role}
                {' '}
                {r.score != null ? `${r.score}%` : '—'}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div><span className="opacity-60">Model tested</span><div className="font-mono">{run.model_tested}</div></div>
          <div><span className="opacity-60">Role</span><div>{ROLE_LABELS[run.model_role]}</div></div>
          <div><span className="opacity-60">Score</span><div className={scoreColor(run.score)}>{run.score != null ? `${run.score}%` : '—'}</div></div>
          <div><span className="opacity-60">Judge</span><div className="font-mono text-xs">{run.evaluation_model}</div></div>
        </div>
        {run.error_message && (
          <div className="mb-4 text-sm">
            <div className="opacity-60 mb-1">Error</div>
            <p className="text-red-500 whitespace-pre-wrap">{run.error_message}</p>
          </div>
        )}
        {run.reasoning && (
          <div className="mb-4 text-sm">
            <div className="opacity-60 mb-1">Judge reasoning</div>
            <p className="opacity-90 whitespace-pre-wrap">{run.reasoning}</p>
          </div>
        )}
        <div className="text-sm mb-2 opacity-60">Prompt</div>
        <pre className={`text-xs bg-theme-secondary/30 p-3 rounded-lg whitespace-pre-wrap mb-4 ${promptExpanded ? '' : 'max-h-32 overflow-hidden'}`}>{promptText || '—'}</pre>
        {promptText?.length > 200 && (
          <button type="button" onClick={() => setPromptExpanded((e) => !e)} className="text-xs text-theme-primary mt-1 mb-4">
            {promptExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
        {schemaText && (
          <>
            <div className="text-sm mb-2 opacity-60">JSON schema</div>
            <pre className={`text-xs bg-theme-secondary/30 p-3 rounded-lg whitespace-pre-wrap mb-4 ${schemaExpanded ? '' : 'max-h-32 overflow-hidden'}`}>{schemaText}</pre>
            {schemaText.length > 200 && (
              <button type="button" onClick={() => setSchemaExpanded((e) => !e)} className="text-xs text-theme-primary mt-1 mb-4">
                {schemaExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}
        <div className="text-sm mb-2 opacity-60">Model response</div>
        <pre className={`text-xs bg-theme-secondary/30 p-3 rounded-lg whitespace-pre-wrap ${responseExpanded ? '' : 'max-h-48 overflow-hidden'}`}>{run.response || '—'}</pre>
        {run.response?.length > 200 && (
          <button type="button" onClick={() => setResponseExpanded((e) => !e)} className="text-xs text-theme-primary mt-1">
            {responseExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
}
